<template>
	<div class="button__wrapper">
		<button
			v-if="!link"
			:class="compiledClasses"
			:aria-label="ariaLabel"
			:disabled="disabled"
			:type="type"
			data-cy="button"
			@click="emitClick"
		>
			<helio-spinner
				v-if="currentState === 'is-loading'"
				size="small"
				:color="color"
			/>

			<helio-icon
				v-if="currentState === 'is-successful'"
				class="button__successful-icon"
				token="check"
				weight="light"
			/>

			<helio-icon
				v-if="currentState === 'is-unsuccessful'"
				class="button__unsuccessful-icon"
				token="times"
				weight="light"
			/>

			<helio-icon
				v-if="iconIsValid && iconPosition !== 'right'"
				class="button__icon"
				:token="icon"
				:weight="iconWeight"
				:color="iconColor"
			/>

			<span
				v-if="text"
				class="button__text"
				v-html="text"
			/>

			<span
				v-else-if="hasDefaultSlot"
				class="button__text"
			>
				<slot />
			</span>

			<span
				v-if="loader"
				class="button__loading-bar"
			/>

			<span
				v-if="loader"
				class="button__loading-success"
			>
				<helio-icon
					class="button__icon"
					token="check"
					weight="light"
				/>
			</span>

			<helio-icon
				v-if="iconIsValid && iconPosition === 'right'"
				class="button__icon"
				:token="icon"
				:weight="iconWeight"
				:color="iconColor"
			/>
		</button>

		<helio-link
			v-if="link"
			:disabled="disabled"
			:link="link"
			:target="target"
			:class="compiledClasses"
			:aria-label="ariaLabel"
			data-cy="button-link"
			@click="emitClick"
		>
			<helio-icon
				v-if="iconIsValid && iconPosition !== 'right'"
				class="button__icon"
				:token="icon"
				:weight="iconWeight"
				:color="iconColor"
			/>

			<span
				v-if="text"
				class="button__text"
			>
				{{ text }}
			</span>

			<span
				v-else-if="hasDefaultSlot"
				class="button__text"
			>
				<slot />
			</span>

			<helio-icon
				v-if="iconIsValid && iconPosition === 'right'"
				class="button__icon"
				:token="icon"
				:weight="iconWeight"
				:color="iconColor"
			/>
		</helio-link>
	</div>
</template>

<script>
import { setTimeout } from 'timers';
import Icon from '../icon/Icon.vue';
import Spinner from '../spinner/Spinner.vue';
import Link from '../link/Link.vue';

export default {
	name: 'Button',

	components: {
		'helio-icon': Icon,
		'helio-spinner': Spinner,
		'helio-link': Link,
	},

	props: {
		animate: {
			type: Boolean,
			required: false,
			default: true,
			description: 'Sets whether the Button will animate on click',
		},

		ariaLabel: {
			type: String,
			required: false,
			default: null,
			description: 'Supplies an aria-label when no button text is provided',
		},

		color: {
			type: String,
			required: false,
			default: 'normal',
			validator: value => [
				'alert',
				'alert-outline',
				'danger',
				'danger-outline',
				'info',
				'info-outline',
				'normal',
				'normal-outline',
				'primary',
				'primary-outline',
				'success',
				'success-outline',
			].indexOf(value) !== -1,
			description: 'Sets the background-color of the Button',
		},

		disabled: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether the Button will be disabled for the user',
		},

		icon: {
			type: String,
			required: false,
			default: '',
			description: 'Sets the icon that will appear in the Button',
		},

		iconColor: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the colour of the icon. The colour provided must be one of those in the $colors-list.',
		},

		iconPosition: {
			type: String,
			required: false,
			default: 'left',
			validator: value => [
				'left',
				'right',
				'top',
			].indexOf(value) !== -1,
			description: 'Sets the location of the icon in the Button',
		},

		iconWeight: {
			type: String,
			required: false,
			default: 'regular',
			validator: value => [
				'bold',
				'brand',
				'light',
				'regular',
			].indexOf(value) !== -1,
			description: 'Sets the weight of the icon',
		},

		link: {
			type: [String, Object],
			required: false,
			default: null,
			description: 'Adds a link on the Button via a string or an object (which may contain link and target properties). A string or link value that begins with / will create an internal router-link. A link that doesn\'t begin with / will create an &lt;a&gt; tag.',
		},

		loader: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Indicates whether the action initiated by the Button click is still loading (with a colour transition) or was successful (with a check mark)',
		},

		loaderDuration: {
			type: Number,
			required: false,
			default: 2000,
			validator: loaderDuration => loaderDuration >= 0,
			description: 'Sets the length of the load time for the loader',
		},

		roundness: {
			type: String,
			required: false,
			default: 'all',
			validator: value => [
				'all',
				'left',
				'right',
				'top',
				'bottom',
				'intense',
				'none',
			].indexOf(value) !== -1,
			description: 'Indicates which corners of the Button will be rounded and how rounded they will be',
		},

		size: {
			type: String,
			required: false,
			default: 'standard',
			validator: value => [
				'large',
				'medium',
				'small',
				'standard',
			].indexOf(value) !== -1,
			description: 'Sets the size of the Button',
		},

		state: {
			type: String,
			required: false,
			default: null,
			validator: value => [
				'is-loading',
				'is-successful',
				'is-unsuccessful',
			].indexOf(value) !== -1,
			description: 'Indicates whether the Button is in a loading state (with a spinner), a successful state (with a check mark), or an unsuccessful state (with an&nbsp;&times;)',
		},

		statusTimer: {
			type: Number,
			required: false,
			default: 3000,
			validator: statusTimer => statusTimer >= 0,
			description: 'Sets countdown time until the state prop is reset to null',
		},

		target: {
			type: String,
			required: false,
			default: '_self',
			validator: value => [
				'_blank',
				'_parent',
				'_self',
				'_top',
				'framename',
			].indexOf(value) !== -1,
			description: 'Sets the location where the linked document will be opened',
		},

		text: {
			type: String,
			required: false,
			default: '',
			description: 'Sets the text that will appear in the Button',
		},

		type: {
			type: String,
			required: false,
			default: 'submit',
			validator: value => [
				'button',
				'reset',
				'submit',
			].indexOf(value) !== -1,
			description: 'Sets the default behaviour of the Button',
		},
	},

	data() {
		return {
			baseClass: 'button',
			loadingState: null,
			currentState: null,
		};
	},

	computed: {
		colorClass() {
			return ` ${this.baseClass}--${this.color}`;
		},

		disabledClass() {
			if (this.disabled) {
				return ' button--disabled';
			}
			return '';
		},

		iconClass() {
			if ((!this.hasDefaultSlot && this.text === '') && this.icon !== '') {
				return ` ${this.baseClass}--icon-only`;
			}
			if (this.iconPosition !== '') {
				return ` ${this.baseClass}--icon-${this.iconPosition}`;
			}
			return '';
		},

		roundedClass() {
			if (this.roundness !== 'all') {
				return ` ${this.baseClass}--rounded-${this.roundness}`;
			}
			return '';
		},

		stateClass() {
			if (this.currentState !== null) {
				return ` ${this.baseClass}--${this.currentState}`;
			}
			return '';
		},

		sizeClass() {
			if (this.size !== 'standard') {
				return ` ${this.baseClass}--${this.size}`;
			}
			return '';
		},

		loadingClass() {
			if (this.loadingState) {
				return ` button--loading-mode ${this.baseClass}--loading-${this.loadingState}`;
			}
			return '';
		},

		compiledClasses() {
			let classString = `${this.baseClass}`;
			classString += `${this.colorClass}`;
			classString += `${this.iconClass}`;
			classString += `${this.roundedClass}`;
			classString += `${this.stateClass}`;
			classString += `${this.sizeClass}`;
			classString += `${this.loadingClass}`;
			classString += `${this.disabledClass}`;
			return classString;
		},

		iconIsValid() {
			return this.icon;
		},

		hasDefaultSlot() {
			return !!this.$slots.default || !!this.$scopedSlots.default;
		},
	},

	watch: {
		loader() {
			if (this.loader) {
				setTimeout(() => {
					this.startLoading();
				}, 10);
			}
		},

		state() {
			if (this.disabled) {
				this.currentState = null;
				return;
			}

			this.currentState = this.state;

			if (this.state === 'is-successful' || this.state === 'is-unsuccessful') {
				setTimeout(() => {
					this.currentState = null;
				}, this.statusTimer);
			}
		},
	},

	methods: {
		emitClick(event) {
			if (this.currentState !== null) {
				return;
			}

			this.$emit('click', event);

			this.clickAnimation();
			if (this.loader) {
				this.startLoading();
			}
		},

		clickAnimation() {
			if (!this.disabled) {
				if (this.animate) {
					this.$el.children[0].setAttribute('button-animating', true);
					setTimeout(() => {
						this.$el.children[0].removeAttribute('button-animating');
					}, 400);
				}
			}
		},

		startLoading() {
			const button = this.$el.children[0];
			this.loadingState = 'started';

			setTimeout(() => {
				this.loadingState = 'ended';

				setTimeout(() => {
					button.classList.add('show-check');
					button.classList.remove('button--loading-ended');

					setTimeout(() => {
						button.classList.add('burst');

						setTimeout(() => {
							button.classList.remove('burst');
							this.loadingState = null;
						}, 500);
					}, 1000);
				}, 1000);
			}, this.loaderDuration);
		},
	},
};
</script>

<style lang="scss" src="./button.scss" />
